import { Resizable } from 'react-resizable';
import 'antd/dist/antd.css';
import '../style/style.css';
import React, { useContext, useState, useRef, useEffect, useCallback } from "react";
import { Table, Input, Button, Space, Checkbox, Form, Popover } from "antd";
import Highlighter from "react-highlight-words";
import { SearchOutlined } from "@ant-design/icons";
import { Link } from 'react-router-dom';
import { UndoOutlined, CloseCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
const EditableContext = React.createContext(null);

const initialColumns = [
  "no",
  "approve",
  "revise",
  "reject",
  "reason",
  "cover_letter_no",
  "claim_number",
  "ref_no",
  "customer_name",
  "program_type",
  "dpp",
  "ppn",
  "total_claim",
  "pph_rate",
  "pph",
  "nett_claim",
  "action"
]

// ======== RESIZE HEADER ===========
const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

// ======== EDIT TABLE INPUT ===========
const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);
  useEffect(() => {
    if (editing) {
      inputRef.current.focus();
    }
  }, [editing]);
  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };
  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({
        ...record,
        ...values,
      });
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };
  let childNode = children;
  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{
          margin: 0,
        }}
        name={dataIndex}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: 24,
        }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }
  return <td {...restProps}>{childNode}</td>;
};

// ======== MAIN TABLE ===========
const Demo = ({ columnsProps, dataProps, dataToApi,stickyHeader }) => {
  const [data, setData] = useState(dataProps);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const tableRef = useRef(null); // Referensi untuk komponen Table

  const [columns, setColumns] = useState([
    {
      title: "No",
      dataIndex: 'no',
      width: 50,
      fixed: 'left',
    },
    {
      title: <Popover placement="topLeft" content={"Approve"}><div className='d-flex justify-content-center'><span><CheckCircleOutlined /></span></div></Popover>,
      width: 70,
      fixed: 'left',
      dataIndex: 'approve',
      render: (_, record) => (
        <div style={{ display: 'flex', justifyContent: 'center', height: '30px', width: '100%' }}>
          <Checkbox
            style={{ alignItems: 'center', height: '100%', width: '100%', display: 'flex', justifyContent: 'center' }}
            checked={record.approve}
            onChange={(e) => optimizedHandleCheckboxChange(e, record, 'approve')}
          />
        </div>
      ),
    },
    {
      title: <Popover placement="topLeft" content={"Revise"}><div className='d-flex justify-content-center'><span><UndoOutlined /></span></div></Popover>,
      width: 70,
      fixed: 'left',
      dataIndex: 'revise',
      render: (_, record) => (
        <div style={{ display: 'flex', justifyContent: 'center', height: '30px', width: '100%' }}>
          <Checkbox
            style={{ alignItems: 'center', height: '100%', width: '100%', display: 'flex', justifyContent: 'center' }}
            checked={record.revise}
            onChange={(e) => optimizedHandleCheckboxChange(e, record, 'revise')}
          />
        </div>
      ),
    },
    {
      title: <Popover placement="topLeft" content={"Reject"}><div className='d-flex justify-content-center'><span><CloseCircleOutlined /></span></div></Popover>,
      width: 70,
      fixed: 'left',
      dataIndex: 'reject',
      render: (_, record) => (
        <div style={{ display: 'flex', justifyContent: 'center', height: '30px', width: '100%' }}>
          <Checkbox
            style={{ alignItems: 'center', height: '100%', width: '100%', display: 'flex', justifyContent: 'center' }}
            checked={record.reject}
            onChange={(e) => optimizedHandleCheckboxChange(e, record, 'reject')}
          />
        </div>
      ),
    },
    {
      title: 'Reason',
      width: 200,
      fixed: 'left',
      dataIndex: 'reason',
      render: (_, record) => (
        <Input
          value={record.reason}
          onBlur={(e) => handleInputChange(e, record)}
          placeholder="Enter reason"
        />
      ),
      editable: true,
    },
    {
      title: 'Cover Letter No',
      dataIndex: 'cover_letter_no',
      width: 150,
      className: 'max-width-col'
    },
    {
      title: 'Claim Number',
      dataIndex: 'claim_number',
      width: 130,
      className: 'max-width-col'
    },

    {
      title: 'Ref No',
      dataIndex: 'ref_no',
      width: 100,
      className: 'max-width-col'
    },
    {
      title: 'Customer Name',
      dataIndex: 'customer_name',
      width: 200,
      className: 'max-width-col'
    },
    {
      title: 'Program Type',
      dataIndex: 'program_type',
      width: 200,
      className: 'max-width-col'
    },
    {
      title: 'Nilai DPP',
      dataIndex: 'dpp',
      width: 150,
      className: 'max-width-col'
    },
    {
      title: 'Nilai PPN',
      dataIndex: 'ppn',
      width: 150,
      className: 'max-width-col'
    },
    {
      title: 'Total Claim',
      dataIndex: 'total_claim',
      width: 150,
      className: 'max-width-col'
    },
    {
      title: 'PPh Rate',
      dataIndex: 'pph_rate',
      width: 150,
      className: 'max-width-col'
    },
    {
      title: 'Nilai PPh',
      dataIndex: 'pph',
      width: 150,
      className: 'max-width-col'
    },
    {
      title: 'Net Claim',
      dataIndex: 'nett_claim',
      width: 150,
      className: 'max-width-col'
    },
    {
      title: 'Action',
      dataIndex: 'action',
      width: 200,
      render: (_,e) => {
        return(
          <Link
            to={`/admin/claim/view/${e.claim_id}`}
            target='_blank'
          >
            View
          </Link>
        )
      },
      sorter: false
    },
  ]);

  useEffect(() => {
    dataToApi(data)
    if (tableRef.current) {
      const tableElement = tableRef.current.querySelector('.ant-table-sticky-holder');
      if(stickyHeader){
        tableElement.style.top = '218px';
      } else{
        tableElement.style.top = '79px';
      }
    }
  }, [stickyHeader,dataProps,data])


  const mappedColumns = columns.map((col) => {
    if (["no", "approve", "revise", "reject", "reason", "action"].includes(col.dataIndex)) {
      return {
        ...col
      };
    } else {
      return {
        ...col,
        ...(
          col.dataIndex && {
            ...getColumnSearchProps(col.dataIndex),
          }
        ),
        sorter: (a, b) => {
          // if (["ref_no", "no", "approve", "revise", "reject", "reason", "action"].includes(col.dataIndex)) {
          if (typeof a[col.dataIndex] === 'number') {
            return a[col.dataIndex] - b[col.dataIndex];
          } else {
            return a[col.dataIndex].localeCompare(b[col.dataIndex]);
          }
        },
        render: (text) => {
          if (typeof text === 'number') {
            return (
              <div style={{textAlign:'right'}}>
                {
                  new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'IDR'
                  }).format(text).replace(/IDR/g, '')
                }
              </div>
            )
          } else {
            return text;
          }
        },
      };
    }
  });

  const handleResize = (index) => (e, { size }) => {
    setColumns((columns) => {
      const nextColumns = [...columns];
      nextColumns[index] = {
        ...nextColumns[index],
        width: size.width,
      };
      return nextColumns;
    });
  };


  //   ==============
  function getColumnSearchProps(dataIndex) {
    return {
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            ref={searchInput}
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value.toString()] : [])
            }
            onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button
              onClick={() => handleReset(clearFilters)}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        record[dataIndex]
          ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
          : null,
      onFilterDropdownVisibleChange: (visible) => {
        if (visible) {
          // setTimeout(() => this.searchInput.select());
        }
      },
      render: (text) =>
        searchedColumn === dataIndex ? (
          <Highlighter
            highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
            searchWords={[searchText]}
            autoEscape
            textToHighlight={text}
          />
        ) : (

          text
        ),
    };
  }

  function handleSearch(selectedKeys, confirm, dataIndex) {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  }

  function handleReset(clearFilters) {
    clearFilters();
    setSearchText("");
  }


  // ================
  const handleCheckboxChange = (e, record, column) => {
    const { checked } = e.target;

    setData(prevData => {
      const newData = prevData.map(item => {
        if (item.key === record.key) {
          // Atur semua kolom ke nilai false kecuali kolom yang diklik
          const updatedItem = { ...item };
          Object.keys(updatedItem).forEach(key => {
            if (key !== column && typeof updatedItem[key] === "boolean") {
              updatedItem[key] = false;
            }
          });
          return { ...updatedItem, [column]: checked };
        } else {
          return item;
        }
      });

      return newData;
    });
  };

  const optimizedHandleCheckboxChange = useCallback(handleCheckboxChange, []);


  const handleInputChange = (e, record) => {
    const { value } = e.target;
    const updatedRecord = {
      ...record,
      reason: value,
    };
    setData((prevData) => prevData.map((item) => (item.key === record.key ? updatedRecord : item)));
  };

  const handleSave = (row) => {
    const newData = [...data];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setData(newData);
  };
  const newMap = mappedColumns.map((col, index) => {
    return {
      ...col,
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleResize(index),
      }),
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  })

  const filteredColumns = columnsProps.length ? newMap.filter((column) => columnsProps.includes(column.dataIndex)) : newMap.filter((column) => initialColumns.includes(column.dataIndex))

  return (
    <>
      <div>
        <Table
          ref={tableRef}
          bordered
          components={{
            header: { cell: ResizableTitle },
            body: {
              row: EditableRow,
              cell: EditableCell,
            },
          }}
          columns={filteredColumns}
          pagination={false}
          dataSource={data}
          scroll={{ x: 'max-content' }}
          sticky
          size="small"
        />
      </div>
    </>
  );
};

export default Demo;
